import { useEffect, useMemo, useRef, useState } from 'react';
import { searchReactions, type ReactionQueryRole, type ReactionSearchHit } from '@peruselab/api-client/reactionSearch';
import { toChemMoleculeV1 } from '@peruselab/structure-editor/domain/moleculeProjection';
import type { BoxedMolecule, ReactionZone } from '@peruselab/structure-editor/domain/reactionGraph';
import type { DrawingDocument } from '@peruselab/structure-editor/model';
import { queryMoleculeOf, type QueryMolecule } from './queryMolecules';
import { useActiveTenant } from '../tenancy/activeTenant';

const DEBOUNCE_MS = 500;

/** The query role each box on the paper stands for (docs/reaction-search.md). */
const ROLE_OF_ZONE: Readonly<Record<ReactionZone, ReactionQueryRole>> = {
  reactants: 'reactant',
  products: 'product',
  above: 'agent',
  below: 'solvent'
};
const ROLES: readonly ReactionQueryRole[] = ['reactant', 'product', 'agent', 'solvent'];

type State =
  | { readonly key: string; readonly status: 'loading' }
  | { readonly key: string; readonly status: 'ready'; readonly hits: readonly ReactionSearchHit[] }
  | { readonly key: string; readonly status: 'error'; readonly message: string };

/**
 * #704 -- with a reaction arrow on the paper, a reaction search: the molecules in each box (left to
 * right; loose molecules are not in the reaction) as one combined query, filtered by the drawn
 * arrow's kind. Debounced, and keyed by chemistry only -- moving things asks nothing new; placing
 * the arrow, or changing what is in a box, does.
 */
export function useReactionSearch(
  document: DrawingDocument,
  molecules: readonly BoxedMolecule[],
  arrowKind: string | null
) {
  const { scopeKey } = useActiveTenant();
  const query = useMemo(() => {
    if (!arrowKind) return null;
    const byRole: Record<ReactionQueryRole, QueryMolecule[]> = { reactant: [], product: [], agent: [], solvent: [] };
    const boxed = molecules
      .filter((molecule) => molecule.zone !== null)
      .toSorted((a, b) => a.bounds.minX - b.bounds.minX || a.bounds.minY - b.bounds.minY);
    for (const molecule of boxed) {
      byRole[ROLE_OF_ZONE[molecule.zone!]].push(queryMoleculeOf(document, molecule.atomIds));
    }
    // #1051: the active tenant is part of the question -- its own reactions are among the answers.
    const key = JSON.stringify([scopeKey, arrowKind, ROLES.map((role) => byRole[role].map((entry) => entry.key))]);
    return { byRole, key };
  }, [arrowKind, document, molecules, scopeKey]);

  const [state, setState] = useState<State | null>(null);
  // Bumped by retry(): re-runs the same search at once (after a failure).
  const [attempt, setAttempt] = useState(0);
  const requestRef = useRef(0);
  const latestQuery = useRef(query);
  useEffect(() => {
    latestQuery.current = query;
  });

  // #743: the arrow alone is a query too (every reaction of its kind), so empty boxes search.
  const key = query ? query.key : null;
  useEffect(() => {
    if (!key) return;
    const request = ++requestRef.current;
    const timeout = globalThis.setTimeout(
      () => {
        const current = latestQuery.current;
        if (!current) return;
        setState({ key, status: 'loading' });
        const documents = (role: ReactionQueryRole) =>
          current.byRole[role].map((entry) => toChemMoleculeV1(entry.document));
        searchReactions({
          reactants: documents('reactant'),
          products: documents('product'),
          agents: documents('agent'),
          solvents: documents('solvent'),
          arrowKinds: arrowKind ? [arrowKind] : []
        })
          .then((results) => {
            if (requestRef.current === request) setState({ key, status: 'ready', hits: results.reactions });
          })
          .catch((error: unknown) => {
            if (requestRef.current === request) {
              setState({
                key,
                status: 'error',
                message: error instanceof Error ? error.message : 'Reaction search failed.'
              });
            }
          });
      },
      attempt === 0 ? DEBOUNCE_MS : 0
    );
    return () => globalThis.clearTimeout(timeout);
  }, [arrowKind, attempt, key]);

  const current = state && state.key === key ? state : null;
  return {
    /** #743: an arrow is on the paper, so the reaction search is the query. */
    active: query !== null,
    /** Ranked reactions for the paper as it is now (empty while loading, or with no arrow). */
    hits: current?.status === 'ready' ? current.hits : [],
    loading: key !== null && current?.status !== 'ready' && current?.status !== 'error',
    /** The search failed (e.g. the server is unreachable or older than the endpoint): say so. */
    failed: current?.status === 'error',
    retry: () => setAttempt((count) => count + 1),
    /** The formula of query molecule `index` in `role`'s box, to explain a match. */
    formulaOf: (role: ReactionQueryRole, index: number) => query?.byRole[role][index]?.formula ?? ''
  };
}
